import { useTranslation } from "react-i18next";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { StoryCostRow } from "@/hooks/useStoryCosts";
import { formatCost } from "@/lib/format";
import { Loader2 } from "lucide-react";

interface StoryCostTableProps {
  rows: StoryCostRow[];
  totalRefs: number;
  loading: boolean;
}

/**
 * Derive the primary model display from the cost_breakdown.by_model array (SD-2).
 * Highest-cost model name + "+N" suffix when multiple models contributed.
 */
function formatModel(
  byModel: Array<{ model: string; cost_usd: number }>,
): { primary: string; extra: number } | null {
  if (byModel.length === 0) return null;
  const sorted = [...byModel].sort((a, b) => b.cost_usd - a.cost_usd);
  return {
    primary: sorted[0]!.model,
    extra: sorted.length - 1,
  };
}

function formatDate(date: string | null, completedAt: string | undefined): string {
  const raw = date ?? completedAt;
  if (!raw) return "--";
  // Take just the date part
  return raw.split("T")[0] ?? raw;
}

export function StoryCostTable({
  rows,
  totalRefs,
  loading,
}: StoryCostTableProps) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        {t("projectPortal.metrics.storyCost.loading")}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-12 text-center">
        <p className="text-sm font-medium text-foreground mb-1">
          {t("projectPortal.metrics.storyCost.empty")}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold tracking-tight">
          {t("projectPortal.metrics.storyCost.heading")}
        </h3>
        {totalRefs > rows.length && (
          <p className="text-xs text-muted-foreground">
            {t("projectPortal.metrics.storyCost.capNote", {
              shown: rows.length,
              total: totalRefs,
            })}
          </p>
        )}
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>
              {t("projectPortal.metrics.storyCost.ref")}
            </TableHead>
            <TableHead>
              {t("projectPortal.metrics.storyCost.model")}
            </TableHead>
            <TableHead>
              {t("projectPortal.metrics.storyCost.cost")}
            </TableHead>
            <TableHead>
              {t("projectPortal.metrics.storyCost.completed")}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => {
            if (row.loading) {
              return (
                <TableRow key={row.ref}>
                  <TableCell>{row.ref}</TableCell>
                  <TableCell colSpan={3} className="text-muted-foreground">
                    <Loader2 className="inline h-3 w-3 animate-spin mr-1" />
                  </TableCell>
                </TableRow>
              );
            }
            if (!row.data) {
              return (
                <TableRow key={row.ref}>
                  <TableCell>{row.ref}</TableCell>
                  <TableCell className="text-muted-foreground">--</TableCell>
                  <TableCell className="text-muted-foreground">--</TableCell>
                  <TableCell className="text-muted-foreground">--</TableCell>
                </TableRow>
              );
            }
            const model = formatModel(row.data.cost_breakdown.by_model);
            return (
              <TableRow key={row.ref}>
                <TableCell className="font-medium">{row.ref}</TableCell>
                <TableCell>
                  {model ? (
                    <>
                      {model.primary}
                      {model.extra > 0 && (
                        <span className="ml-1 text-xs text-muted-foreground">
                          +{model.extra}
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="text-muted-foreground">--</span>
                  )}
                </TableCell>
                <TableCell>{formatCost(row.data.total_cost_usd)}</TableCell>
                <TableCell>
                  {formatDate(row.data.date, row.data.completed_at)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
